import React from 'react';
import axios from 'axios';
import ReactGA from 'react-ga';
import { Button, Icon, Input, Label, Modal } from 'semantic-ui-react';
import { Elements, StripeProvider } from 'react-stripe-elements';
import DomainSearchForm from './DomainSearchForm';
import PaymentOptions from './PaymentOptions';
import CheckoutForm from './CheckoutForm';
import Completion from './Completion';
import PeriodSelect from './PeriodSelect';
import { CUSTOM_PLAN_INDEX } from '../data/payment-types';
import { cleanDomainName, getBusinessEmailServiceURL } from '../../../utils/urls';

export default class PublishModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentDomain: null,
      enteredDomain: '',
      customDomain: null,
      customDomainInputError: false,
      hasCustomDomain: false,
      currentPlanIndex: null,
      currentPlanPeriod: null,
      email: '',
      emailSubmitted: false,
    };

    this.stripeApiKey = process.env.STRIPE_API_KEY || process.env.STORYBOOK_STRIPE_API_KEY;
    this.handleDomainChange = this.handleDomainChange.bind(this);
    this.onDomainSelected = this.onDomainSelected.bind(this);
    this.onHasCustomDomainChanged = this.onHasCustomDomainChanged.bind(this);
    this.onCustomDomainSelected = this.onCustomDomainSelected.bind(this);
    this.onPlanSelected = this.onPlanSelected.bind(this);
    this.onPeriodSelected = this.onPeriodSelected.bind(this);
    this.handleEmailChange = this.handleEmailChange.bind(this);
    this.onSubmitEmail = this.onSubmitEmail.bind(this);
  }

  onDomainSelected(newDomain) {
    this.setState({ currentDomain: newDomain }, () => {
      const { currentDomain } = this.state;
      const { business } = this.props;
      const { type: businessType, id: businessId } = business;

      const url = getBusinessEmailServiceURL(businessType, businessId);

      axios.post(url, {
        domain_name: currentDomain.DomainName,
        email_type: 'publish_modal_domain_selected',
      });
    });
    this.logEvent('Domain Selected');
  }

  onCustomDomainSelected(newCustomDomain) {
    if (newCustomDomain === null) {
      this.setState({ customDomain: newCustomDomain, currentDomain: newCustomDomain });
    }
    if (newCustomDomain.length > 3) {
      this.setState(
        {
          customDomain: newCustomDomain,
          currentDomain: newCustomDomain,
          customDomainInputError: false,
        },
        () => {
          const { customDomain } = this.state;
          const { business } = this.props;
          const { type: businessType, id: businessId } = business;

          const url = getBusinessEmailServiceURL(businessType, businessId);

          axios.post(url, {
            domain_name: customDomain,
            email_type: 'publish_modal_custom_domain_selected',
          });
        },
      );
    } else {
      this.setState({ customDomainInputError: true });
    }
    this.logEvent('Existing Domain Selected');
  }

  onHasCustomDomainChanged() {
    this.setState(({ hasCustomDomain }) => ({ hasCustomDomain: !hasCustomDomain }));
  }

  onPlanSelected(newPlanIndex) {
    this.setState({ currentPlanIndex: newPlanIndex });
    this.logEvent('Payment Plan Selected');
  }

  onPeriodSelected(newStatus) {
    this.setState(() => ({ currentPlanPeriod: newStatus }));
    this.logEvent('Plan Period Selected');
  }

  onSubmitEmail(back = false) {
    const { business } = this.props;
    const { type: businessType, id: businessId } = business;
    const { email } = this.state;

    if (back) {
      this.setState({ emailSubmitted: false });
    } else {
      this.setState({ emailSubmitted: true });

      const url = getBusinessEmailServiceURL(businessType, businessId);

      axios.post(url, {
        email,
        email_type: 'publish_modal_submit_email',
      });
    }

    this.logEvent('Email Submitted');
  }

  handleDomainChange(e, { value }) {
    this.setState({ enteredDomain: cleanDomainName(value) });
  }

  handleEmailChange(e, { value }) {
    this.setState({ email: value });
  }

  logEvent(step) {
    const { business } = this.props;
    ReactGA.event({
      category: 'Checkout',
      action: `Step ${step}`,
      label: business.name,
    });
  }

  renderHasExistingDomain() {
    const { collectEmail } = this.props;
    const { enteredDomain, customDomainInputError } = this.state;

    return (
      <React.Fragment>
        <Button id="domain-back-button" className="back-button" style={{ cursor: 'inherit' }}>
          <Icon />
          Step {collectEmail ? '1/5' : '1/4'}
        </Button>
        <Modal.Header className="modal-header centered-content">
          Enter your custom domain
        </Modal.Header>
        <p className="modal-subheader centered-content body-text blue-color">
          Connect your custom domain to your new site
        </p>
        <Modal.Content className="modal-body">
          <div>
            <Input
              className="domain-search-input domain-search-input-full-width custom-domain-input"
              type="text"
              placeholder="mydomain.com"
              action
              labelPosition="left"
              value={enteredDomain}
              onChange={this.handleDomainChange}
            >
              <Label className="https-label domain-search-input-consistent-padding">https://</Label>
              <input className="borderless-input domain-search-input-consistent-padding" />
              <Button
                id="domain-connect-button"
                className="domain-search-button centered-button"
                type="submit"
                onClick={() => this.onCustomDomainSelected(enteredDomain)}
              >
                Connect
              </Button>
            </Input>
            {customDomainInputError && (
              <p className="error-message">Please enter a valid domain.</p>
            )}
            <p className="modal-subheader body-text blue-color">
              Please allow 2-3 business days to process and connect your custom domain to your new
              published site
            </p>
            <Button
              id="custom-domain-connect-button"
              className="publish-now-from-request-button extra-margin centered-content blue-color"
              onClick={this.onHasCustomDomainChanged}
            >
              I want to search for a new domain.
            </Button>
          </div>
        </Modal.Content>
      </React.Fragment>
    );
  }

  renderDomainSearch() {
    const { defaultDomain, collectEmail } = this.props;
    return (
      <React.Fragment>
        <Button id="domain-back-button" className="back-button" style={{ cursor: 'inherit' }}>
          <Icon />
          Step {collectEmail ? '1/5' : '1/4'}
        </Button>
        <Modal.Header className="modal-header centered-content">
          Search for a custom domain for your site
        </Modal.Header>
        <p className="modal-subheader centered-content body-text blue-color">
          Your domain will be your website’s URL address
        </p>
        <Modal.Content className="modal-body">
          <DomainSearchForm
            defaultDomain={defaultDomain}
            onDomainSelected={this.onDomainSelected}
            onHasCustomDomainChanged={this.onHasCustomDomainChanged}
          />
        </Modal.Content>
      </React.Fragment>
    );
  }

  renderSubmitEmail() {
    const { email } = this.state;

    return (
      <React.Fragment>
        <Button
          id="checkout-back-button"
          className="back-button"
          onClick={() => this.onCustomDomainSelected(null)}
        >
          <Icon name="caret left" />
          Step 2/5
        </Button>
        <Modal.Header className="modal-header">Please submit your email.</Modal.Header>
        <p className="modal-subheader centered-content body-text blue-color">
          We&apos;ll use this to contact you about updates to your website.
        </p>
        <Modal.Content className="email-submission-container">
          <Input value={email} onChange={this.handleEmailChange} placeholder="Your Email" />
          <Button onClick={() => this.onSubmitEmail(false)}>Submit</Button>
        </Modal.Content>
      </React.Fragment>
    );
  }

  renderPaymentOptions() {
    const { collectEmail } = this.props;

    return (
      <React.Fragment>
        <Button
          id="checkout-back-button"
          className="back-button"
          onClick={() =>
            collectEmail ? this.onSubmitEmail(true) : this.onCustomDomainSelected(null)
          }
        >
          <Icon name="caret left" />
          Step {collectEmail ? '3/5' : '2/4'}
        </Button>
        <Modal.Header className="modal-header">Let&#39;s finalize your site!</Modal.Header>
        <Modal.Content>
          <PaymentOptions onPlanSelected={this.onPlanSelected} />
        </Modal.Content>
      </React.Fragment>
    );
  }

  renderPeriodSelection() {
    const { collectEmail } = this.props;
    const { currentPlanIndex } = this.state;

    return (
      <React.Fragment>
        <Button
          id="checkout-back-button"
          className="back-button"
          onClick={() => this.onPlanSelected(null)}
        >
          <Icon name="caret left" />
          Step {collectEmail ? '4/5' : '3/4'}
        </Button>
        <PeriodSelect
          currentPlanIndex={currentPlanIndex}
          onPeriodSelected={this.onPeriodSelected}
        />
      </React.Fragment>
    );
  }

  renderCheckoutForm() {
    const { currentPlanIndex, currentPlanPeriod, currentDomain, customDomain, email } = this.state;
    const { business, onCheckoutCompleted, collectEmail } = this.props;
    return (
      <React.Fragment>
        <Button
          id="checkout-back-button"
          className="back-button"
          onClick={() => this.onPeriodSelected(null)}
        >
          <Icon name="caret left" />
          Step {collectEmail ? '5/5' : '4/4'}
        </Button>
        <StripeProvider apiKey={this.stripeApiKey}>
          <Elements>
            <CheckoutForm
              email={email}
              business={business}
              currentPlanIndex={currentPlanIndex}
              isAnnual={currentPlanPeriod}
              onCheckoutCompleted={onCheckoutCompleted}
              currentDomain={currentDomain}
              customDomain={customDomain}
            />
          </Elements>
        </StripeProvider>
      </React.Fragment>
    );
  }

  renderCompletion() {
    this.logEvent('Publish Completion');
    const { handlePublishClose } = this.props;

    return (
      <React.Fragment>
        <Completion
          title="Payment Complete"
          description="A confirmation email will be sent to you shortly"
        />
        <Button
          className="payment-publish-button centered-button extra-margin"
          onClick={handlePublishClose}
        >
          View published site
        </Button>
      </React.Fragment>
    );
  }

  renderAdvancedCompletion() {
    this.logEvent('Publish Advanced Plan Completion');

    return (
      <React.Fragment>
        <Completion
          title="Advanced Payment Selected"
          description="A confirmation email will be sent to you shortly"
        />
      </React.Fragment>
    );
  }

  render() {
    const {
      currentDomain,
      hasCustomDomain,
      currentPlanIndex,
      currentPlanPeriod,
      emailSubmitted,
    } = this.state;
    const {
      showPublishModal,
      handlePublishOpen,
      handlePublishClose,
      checkedOut,
      collectEmail,
    } = this.props;

    let modalBody;

    if (!checkedOut && currentDomain === null) {
      modalBody = hasCustomDomain ? this.renderHasExistingDomain() : this.renderDomainSearch();
    }

    if (!checkedOut && currentDomain !== null && currentPlanPeriod === null) {
      modalBody =
        currentPlanIndex === null ? this.renderPaymentOptions() : this.renderPeriodSelection();
    }

    if (!emailSubmitted && currentDomain !== null && collectEmail) {
      modalBody = this.renderSubmitEmail();
    }

    if (
      !checkedOut &&
      currentDomain !== null &&
      (currentPlanIndex === null || currentPlanIndex < CUSTOM_PLAN_INDEX) &&
      currentPlanPeriod !== null
    ) {
      modalBody = this.renderCheckoutForm();
    }

    if (checkedOut) {
      modalBody =
        currentPlanIndex === CUSTOM_PLAN_INDEX
          ? this.renderAdvancedCompletion()
          : this.renderCompletion();
    }

    return (
      <Modal
        trigger={
          <Button id="publish-now-button" className="payment-publish-button">
            Sign Up
          </Button>
        }
        className="modal-container"
        open={showPublishModal}
        dimmer="inverted"
        onOpen={handlePublishOpen}
        onClose={handlePublishClose}
      >
        <Button
          id="inside-close-icon"
          className="inside-close-icon"
          onClick={handlePublishClose}
          icon
        >
          <Icon name="close" />
        </Button>
        {modalBody}
      </Modal>
    );
  }
}
